import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { take, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { ArrayUtils } from '../../utils/array.utils';
import { CustomerFacade } from '../../facades/customer.facade';
import { AnalyticsService } from '../../analytics/analytics.service';
import { PageTypes } from '../../analytics/enums/pageTypes';
import {
  EFeatureToggles,
  EPurchaseActivityTabs,
  EStoreTypes,
  EUserRoles,
  HospitalOrdersTabs,
} from '../../configurations/common';
import { CheckoutFacade } from '../../facades/checkout.facade';
import { IRfqBaseInfo, IRfqCancelDetails, IRfqCart, IRfqCartItem } from '../../models/rfq.models';
import { UiUtils } from '../../utils/ui.utils';
import { EQuoteRequestStatus, EScenarioType } from '../../configurations/order-status';
import { StoreConfigurationFacade } from '../../facades/store-configuration.facade';
import { CartUtils } from '../../utils/cart.utils';
import { ConfigurationFacade } from '../../facades/configuration.facade';
import { AppUtils } from '../../utils/app.utils';
import { PurchaseActivityFacade } from '../../features/purchase-activity/facade/purchase-activity.facade';
import { StringUtils } from '../../utils/string.utils';
import { ImageUtils } from '../../utils/image.utils';
import { ImagePreset } from '../../models/image.models';

@Component({
  selector: 'app-page-request-tracking-history',
  templateUrl: './page-request-tracking-history.component.html',
  styleUrls: ['./page-request-tracking-history.component.scss'],
  standalone: false,
})
export class PageRequestTrackingHistoryComponent implements OnInit, OnDestroy {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private customerFacade: CustomerFacade = inject(CustomerFacade);
  private analyticsService: AnalyticsService = inject(AnalyticsService);
  private checkoutFacade: CheckoutFacade = inject(CheckoutFacade);
  private storeConfigurationFacade: StoreConfigurationFacade = inject(StoreConfigurationFacade);
  private configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);
  private purchaseActivityFacade: PurchaseActivityFacade = inject(PurchaseActivityFacade);

  quoteDetails!: IRfqBaseInfo;
  totalQuantity: number = 0;
  estimatedArrivalDate!: string;
  isLoaded!: boolean;
  companyRoles!: EUserRoles[];
  showModalCancelOrder: boolean = false;
  orderId!: string;
  isRfqOnly: boolean = false;
  isRequestQuoteClosedLoopEnabled: boolean = false;
  quoteRequestType: string = EScenarioType.REQUEST;
  generatedQuoteType: string = EScenarioType.GENERATED_QUOTE;

  private unsubscribe$: Subject<void> = new Subject<void>();

  protected readonly EStoreTypes: typeof EStoreTypes = EStoreTypes;
  protected readonly AppUtils: typeof AppUtils = AppUtils;
  protected readonly ImageUtils: typeof ImageUtils = ImageUtils;
  protected readonly ImagePreset: typeof ImagePreset = ImagePreset;

  //Feature toggles
  isHarmonizedPurchaseActivityEnabled$: Observable<boolean> =
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.HARMONIZED_PURCHASE_ACTIVITY_PAGE);

  ngOnInit(): void {
    this.isClosedLoopFeatureEnabled();
    this.selectCompanyRoles();
    this.selectIsRfqOnly();
    this.retrieveQuoteRequestDetails(this.route.snapshot.paramMap.get('requestId') ?? '');
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  retrieveQuoteRequestDetails(id: string): void {
    this.isLoaded = false;
    this.checkoutFacade.selectQuoteDetails(id).pipe(take(1)).subscribe({
      next: data => {
        if (data) {
          this.quoteDetails = this._modifyQuoteDetailsItems(data.data);
          const date: number = Date.parse(this.quoteDetails.attributes.createdAt);
          const resultDate: Date = new Date(date);
          resultDate.setDate(resultDate.getDate() + 2);
          this.estimatedArrivalDate = resultDate.toString();
          const items: IRfqCartItem[] = Array.from(Object.create(this.quoteDetails.attributes.shownVersion.cart.items));
          this.quoteDetails.attributes.shownVersion.cart.items = this.combineSameItems(items);
          this.setAnalyticsInfo();
          this.totalQuantity = items.reduce((prev, curr) => {
            return prev + (curr.quantity ?? 0);
          }, 0);
          this.orderId = this.quoteDetails.id ?? '';
        }
      },
      complete: () => {
        this.isLoaded = true;
      },
    });
  }

  /**
   * modify quote order items to array of items if it is not an array (when single item is in cart)
   * @param {IRfqBaseInfo} order
   * @returns {IRfqBaseInfo}
   * @private
   */
  private _modifyQuoteDetailsItems(order: IRfqBaseInfo): IRfqBaseInfo {
    let orderCart: IRfqCart = order.attributes.shownVersion.cart;
    if (typeof orderCart.items['1'] === 'object' && orderCart.items.length === undefined) {
      orderCart = {...orderCart, items: [orderCart.items['1']]};
    }
    return {
      ...order,
      attributes: {
        ...order.attributes,
        shownVersion: {
          ...order.attributes.shownVersion,
          cart: orderCart,
        },
      },
    };
  }

  setAnalyticsInfo(): void {
    this.analyticsService.setProducts(this.quoteDetails.attributes.shownVersion.cart.items);
    this.analyticsService.setCartId(this.quoteDetails.id ?? '');
    this.analyticsService.trackOrderApproved('Order Details', PageTypes.ORDER_DETAILS);
  }

  combineSameItems(items: IRfqCartItem[]): IRfqCartItem[] {
    return ArrayUtils.uniqueObjects(items, 'sku');
  }

  backToOrders(): void {
    this.isHarmonizedPurchaseActivityEnabled$.subscribe(
      isHarmonizedPurchaseActivityPageEnabled => {
        if (isHarmonizedPurchaseActivityPageEnabled) {
          this.router.navigate(['/purchase-activity']);
        } else {
          let queryParams: {tab: HospitalOrdersTabs};
          if (this.isGeneratedQuoteWorkflow()) {
            queryParams = {tab: HospitalOrdersTabs.PREVIOUS_QUOTES};
          } else {
            queryParams = {tab: HospitalOrdersTabs.PREVIOUS_REQUESTS};
          }
          this.router.navigate(['/my-orders'], {queryParams});
        }
      },
    );
  }

  selectCompanyRoles(): void {
    this.customerFacade.getCustomerCompanyRoles().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(companyRoles => {
      this.companyRoles = companyRoles;
    });
  }

  selectIsRfqOnly(): void {
    this.storeConfigurationFacade.selectIsRfqOnly().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(state => {
      this.isRfqOnly = state;
    });
  }

  isApprover(): boolean {
    return this.companyRoles.includes(EUserRoles.Approver) || this.companyRoles.includes(EUserRoles.Admin);
  }

  cancelQuoteRequestModal(): void {
    if (this.quoteDetails.attributes.status === EQuoteRequestStatus.REQUESTED) {
      this.showModalCancelOrder = true;
    }
  }

  cancelQuoteRequest(orderId: string): void {
    const data: IRfqCancelDetails = {
      data: {
        type: 'quote-request-cancel',
        attributes: {},
      },
    };
    this.checkoutFacade.cancelRequest(orderId, data)
      .pipe(take(1))
      .subscribe({
          next: () => {
            this.purchaseActivityFacade.clearPurchaseActivitySection(EPurchaseActivityTabs.PREVIOUS_REQUESTS);
            this.showModalCancelOrder = false;
            this.backToOrders();
          },
          error: () => {
            this.showModalCancelOrder = false;
          },
        },
      );
  }

  restartShowModal(): void {
    this.showModalCancelOrder = false;
  }

  statusIcon(): string {
    const status: string = this.isRequestQuoteClosedLoopEnabled && this.quoteDetails.attributes.shownVersion.status !== null
      ? this.quoteDetails.attributes.shownVersion.status
      : this.quoteDetails.attributes.status;
    return UiUtils.getStatusIcon(status);
  }

  cancelButtonDisabled(): boolean {
    return (this.quoteDetails.attributes.status !== EQuoteRequestStatus.REQUESTED || this.quoteDetails.attributes.shownVersion.status !== null);
  }

  isGeneratedQuoteWorkflow(): boolean {
    return this.isRfqOnly && CartUtils.isSetPricesForAllItems(this.quoteDetails.attributes.shownVersion.cart.items);
  }

  getTranslationGroup(): string {
    return this.isGeneratedQuoteWorkflow() ? 'quote-tracking' : 'request-tracking';
  }

  /**
   * Check if Request Quote Closed Loop feature is enabled
   */
  isClosedLoopFeatureEnabled(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.REQUEST_QUOTE_CLOSED_LOOP)
      .pipe(take(1))
      .subscribe((isEnabled: boolean) => {
        this.isRequestQuoteClosedLoopEnabled = isEnabled;
      });
  }

  /**
   * Creates translation key based on given quote request version status
   */
  getVersionStatusTranslationKey(status: string): string {
    return `request-tracking.status-bar-version-status-${StringUtils.replaceSpaces(status ?? '', '-')}-title`;
  }
}
